import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import agentProfileSvg from "shared/icons/boomLeasingAgentProfile.svg";
import { instance } from "api/apiBase";
import { cx, formatPhone } from "shared/utils";
import { getErrorMessage } from "utils/error";
import { formatCalendar } from "../helpers/format-calendar";
import { Notification } from "../types";

// The backend builds the same link in Notification#url; this is the fallback
// for a row that arrived without one. Keep in sync with the backend builder.
const buildEscalationUrl = (metadata: Notification["metadata"]): string =>
  metadata.call_transcript_id
    ? `/calls?tab=log&call=ai_transcript:${metadata.call_transcript_id}`
    : "/calls?tab=log";

const Dot = () => (
  <span className="size-1 shrink-0 rounded-full bg-content-100" />
);

/**
 * An AI agent call the agent escalated (CRM-1472). Opening it marks it read
 * and lands on that call's drawer in Calls → Call log.
 */
export const AiEscalationNotificationCard = ({
  item
}: {
  item: Notification;
}) => {
  const queryClient = useQueryClient();
  const { id, read_at, url, metadata, created_at } = item;

  const { mutate: markAsRead } = useMutation({
    mutationKey: ["NOTIFICATIONS", "MARK_AS_READ"],
    mutationFn: (ids: number[]) =>
      instance.post("/portal/v1/notifications/mark_as_read", { ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["NOTIFICATIONS"] });
      queryClient.invalidateQueries({
        queryKey: ["UNREAD_NOTIFICATIONS_COUNT"]
      });
      queryClient.invalidateQueries({
        queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
      });
      queryClient.invalidateQueries({ queryKey: ["NEWEST_NOTIFICATIONS"] });
    },
    onError: (error) => toast.error(getErrorMessage(error))
  });

  const caller = metadata.caller_number
    ? formatPhone(metadata.caller_number)
    : "Unknown caller";

  return (
    <Link
      to={url || buildEscalationUrl(metadata)}
      onClick={() => {
        if (!read_at) markAsRead([id]);
      }}
      aria-label={`Escalation signal detected, caller ${caller}${read_at ? "" : ", unread"}`}
      className="flex items-center gap-4 bg-white p-6 hover:bg-alpha-40"
    >
      <span
        aria-hidden="true"
        className={cx(
          "size-3 shrink-0 rounded-full",
          read_at ? "bg-alpha-40" : "bg-primary-500"
        )}
      />
      <img
        src={agentProfileSvg}
        alt=""
        width={44}
        height={44}
        className="shrink-0 rounded-full"
      />
      <div className="min-w-0 space-y-1">
        <p className="truncate text-base font-bold text-content-1000">
          Escalation signal detected
        </p>
        <p className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-medium text-content-400">
          <span>Caller: {caller}</span>
          <Dot />
          <span>Agent: AI Leasing Agent</span>
          <Dot />
          <span>{formatCalendar(metadata.occurred_at || created_at)}</span>
        </p>
      </div>
    </Link>
  );
};
